import React, { FC } from "react";
import {
  Typography,
  Button,
  Modal,
  TextField,
  TextareaAutosize,
  Divider,
  Stack,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  AttachFile as AttachIcon,
  Send as SendIcon,
  Close as CloseIcon,
  AddCircle as AddIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { StyledModalDiv } from "../../../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useCustomerEmailSelector from "./useCustomerEmailSelector";

const StyledTextArea = styled(TextareaAutosize)({
  width: 400,
  height: 100,
  padding: 10,
  maxWidth: 500,
  minWidth: 200,
  maxHeight: 400,
  minHeight: 100,
});

const StyledDivCol = styled("div")({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
});

const StyledDivRow = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const StyledDivRowStart = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 5,
  width: "100%",
});

const StyledDivRowEnd = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: 140,
  paddingRight: 20,
});

interface CustomerEmailProps {
  customerEmail: string;
  dealerEmail?: string;
  fileName: string;
  open: boolean;
  handleClose: () => void;
  type?: string;
  handlePDF: () => void;
  pdfObj: { [key: string]: any };
  pdfUrl?: string;
  loadPdf?: boolean;
}

const CustomerEmailModal: FC<CustomerEmailProps> = ({
  customerEmail,
  dealerEmail,
  fileName,
  open,
  handleClose,
  type,
  handlePDF,
  pdfObj,
  pdfUrl,
  loadPdf,
}) => {
  const {
    handleSend,
    handleChange,
    removeCC,
    removeRecipient,
    allowEnterCC,
    allowEnterTo,
    showCC,
    showTo,
    handleChangeFilename,
    handleCloseSnack,
    openSnack,
    error,
    load,
    editFile,
    filename,
    emailBody,
    subject,
    showCCTextBox,
    newCC,
    cc,
    showToTextBox,
    newTo,
    to,
    modalStyle,
    setNewTo,
    addRecipient,
    setNewCC,
    addCC,
    setSubject,
    setEditFile,
    setBody,
  } = useCustomerEmailSelector(
    pdfObj,
    customerEmail,
    dealerEmail || "",
    fileName,
    open,
    handleClose,
    type
  );

  const body = (
    <StyledModalDiv style={{ ...modalStyle, width: 600 }}>
      <StyledDivCol>
        <StyledDivCol style={{ padding: 10, marginBottom: 15 }}>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            {type === "insights"
              ? "Email Insights"
              : "Email Quote Directly to Customer"}
          </Typography>
          {!type && (
            <Typography variant="caption">
              *email will be sent from no-reply@agmillworks.com
            </Typography>
          )}
          <Divider
            style={{ width: "100%", paddingTop: 5 }}
            orientation="horizontal"
          />
        </StyledDivCol>
        <StyledDivRowStart>
          <StyledDivRowEnd style={{ width: 100 }}>
            <Typography style={{ fontWeight: "bold" }}>To:</Typography>
          </StyledDivRowEnd>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            style={{ minWidth: 240 }}
          >
            {to.length
              ? to.map((email, key) => (
                  <Chip
                    key={key}
                    label={email}
                    variant="outlined"
                    onDelete={() => removeRecipient(email)}
                  />
                ))
              : null}
            {showToTextBox ? (
              <StyledDivRow style={{ paddingBottom: 10, width: "100%" }}>
                <TextField
                  inputProps={{ style: { paddingTop: 2, paddingBottom: 2 } }}
                  size="small"
                  value={newTo}
                  onChange={handleChange(setNewTo)}
                  style={{ maxWidth: 225 }}
                  onKeyDown={allowEnterTo}
                />
                <IconButton onClick={addRecipient}>
                  <CheckIcon color="success" />
                </IconButton>
              </StyledDivRow>
            ) : (
              <IconButton onClick={showTo}>
                <AddIcon color="primary" />
              </IconButton>
            )}
          </Stack>
        </StyledDivRowStart>

        <StyledDivRowStart>
          <StyledDivRowEnd style={{ width: 100 }}>
            <Typography style={{ fontWeight: "bold" }}>CC:</Typography>
          </StyledDivRowEnd>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            style={{ minWidth: 240 }}
          >
            {cc.length
              ? cc.map((email, key) => (
                  <>
                    {email === dealerEmail ? (
                      <Chip
                        key={key}
                        label={email}
                        variant="outlined"
                        color={"cyan" as "primary"}
                      />
                    ) : email === dealerEmail ? (
                      <Chip
                        key={key}
                        label={email}
                        variant="outlined"
                        color={"cyan" as "primary"}
                      />
                    ) : (
                      <Chip
                        key={key}
                        label={email}
                        variant="outlined"
                        color={"cyan" as "primary"}
                        onDelete={() => removeCC(email)}
                      />
                    )}
                  </>
                ))
              : null}
            {showCCTextBox ? (
              <StyledDivRow style={{ paddingBottom: 10, width: "100%" }}>
                <TextField
                  inputProps={{ style: { paddingTop: 2, paddingBottom: 2 } }}
                  size="small"
                  value={newCC}
                  onChange={handleChange(setNewCC)}
                  style={{ maxWidth: 225 }}
                  onKeyDown={allowEnterCC}
                />
                <IconButton onClick={addCC}>
                  <CheckIcon color="success" />
                </IconButton>
              </StyledDivRow>
            ) : (
              <IconButton onClick={showCC}>
                <AddIcon color="primary" />
              </IconButton>
            )}
          </Stack>
        </StyledDivRowStart>

        <StyledDivRowStart style={{ paddingTop: 10 }}>
          <StyledDivRowEnd style={{ width: 100 }}>
            <Typography style={{ fontWeight: "bold" }}>Subject:</Typography>
          </StyledDivRowEnd>

          <TextField
            size="small"
            value={subject}
            onChange={handleChange(setSubject)}
            style={{ maxWidth: 225 }}
          />
        </StyledDivRowStart>

        <StyledDivRowStart style={{ paddingTop: 10 }}>
          <StyledDivRowEnd>
            <AttachIcon />
            <Typography style={{ fontWeight: "bold" }}>Attachment:</Typography>
          </StyledDivRowEnd>

          <StyledDivRow>
            {!editFile ? (
              <>
                {pdfUrl ? (
                  <a
                    style={{ textDecoration: "none", color: "black" }}
                    href={pdfUrl}
                    download={fileName}
                  >
                    <Typography>{filename}.pdf</Typography>
                  </a>
                ) : (
                  <button
                    style={{ textDecoration: "none", color: "black" }}
                    onClick={handlePDF}
                    disabled={loadPdf || false}
                  >
                    <Typography>{filename}.pdf</Typography>
                  </button>
                )}

                <Tooltip title="Change File Name" placement="top">
                  <IconButton onClick={() => setEditFile(true)}>
                    <EditIcon color={"cyan" as "primary"} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <StyledDivRow style={{ paddingBottom: 10, width: "100%" }}>
                <TextField
                  inputProps={{ style: { paddingTop: 2, paddingBottom: 2 } }}
                  size="small"
                  value={filename}
                  onChange={handleChangeFilename}
                  style={{ maxWidth: 320 }}
                  onKeyDown={(e) =>
                    e.key === "Enter" ? setEditFile(false) : null
                  }
                />
                <IconButton onClick={() => setEditFile(false)}>
                  <CheckIcon color="success" />
                </IconButton>
              </StyledDivRow>
            )}
          </StyledDivRow>
        </StyledDivRowStart>

        <StyledDivRow style={{ paddingTop: 20 }}>
          <StyledTextArea
            value={emailBody}
            onChange={handleChange(setBody)}
            placeholder="Email Body..."
          />
        </StyledDivRow>

        {error && (
          <StyledDivRow style={{ padding: 10 }}>
            <Typography
              variant="subtitle1"
              style={{ color: "red", fontWeight: "bold" }}
            >
              A problem occured sending the email
            </Typography>
          </StyledDivRow>
        )}

        <StyledDivRow style={{ paddingTop: 40 }}>
          <Button
            color="inherit"
            onClick={handleClose}
            startIcon={<CloseIcon />}
            variant="contained"
            style={{ marginRight: 40 }}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            endIcon={<SendIcon />}
            style={{ marginLeft: 40 }}
            disabled={load}
            size="large"
          >
            {load ? "Sending..." : "Send"}
          </Button>
        </StyledDivRow>
      </StyledDivCol>
    </StyledModalDiv>
  );

  return (
    <>
      <Snackbar
        open={openSnack}
        autoHideDuration={5000}
        onClose={handleCloseSnack}
      >
        <Alert
          onClose={handleCloseSnack}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your email has been sent!
        </Alert>
      </Snackbar>

      <Modal open={open} onClose={handleClose}>
        {body}
      </Modal>
    </>
  );
};

export default CustomerEmailModal;
